import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { TradingEntity, EntityType, EntityStatus, OrderDetail, MarketType } from 'api'
import { Locale, Translation } from 'locale'
import { TradingEntityHelper, TradingEntityWithAliasHelper } from 'model'
import { createEntityGetterSelector } from 'selectors/entitySelectors'
import {
  createFundListByInstrumentSelector,
  createOrganisationTradingEntityListSelector,
  createDealTradingEntityListSelector,
  createTradingEntityWithOptionalAliasGetterSelector,
  createUserTradingEntityListSelector,
  createTradingEntityListForStagingMyEntitySelector
} from 'selectors/tradingEntitySelectors/tradingEntitySelectors'
import { Field } from 'view/components/inputs'
import { Combobox, ComboboxProps, MultiCombobox, MultiComboboxProps } from 'view/components/options'

/*
 *
 * Fund Dropdowns
 *
 */

/* Default Fund without optionKeys */

const FundComboboxDefaultProps = {
  getOptionText: TradingEntityHelper.getName,
  getOptionDisabled: (tradingEntity: TradingEntity) => tradingEntity.status === EntityStatus.DISABLED,
  noItemsText: Locale.translate(Translation.SC_N_FUNDS, { count: 0 }),
  filterFields: ['shortName']
}

const defaultFundMapStateToProps = createStructuredSelector({
  getOption: createEntityGetterSelector(EntityType.TRADING_ENTITY)
})

export const DefaultFundCombobox = connect(defaultFundMapStateToProps, null, null, { forwardRef: true })(
  Combobox as React.ComponentType<ComboboxProps<TradingEntity, string>>
)
export const DefaultFundComboboxField = Field(DefaultFundCombobox)
DefaultFundCombobox.defaultProps = FundComboboxDefaultProps

export interface FundComboboxProps extends ComboboxProps<TradingEntity, string> {
  instrumentId?: string | null
}

/* Funds by Instrument */
export const FundCombobox = connect(
  createStructuredSelector({
    optionKeys: createFundListByInstrumentSelector((_: {}, props: FundComboboxProps) => props.instrumentId)
  }),
  null,
  null,
  { forwardRef: true }
)(DefaultFundCombobox) as React.ComponentType<Omit<FundComboboxProps, 'optionKeys'>>
export const FundComboboxField = Field(FundCombobox)

/* Funds by OrderDetail */
interface OrderMyEntityComboboxProps extends ComboboxProps<TradingEntity, string> {
  orderDetail: OrderDetail
}
export const OrderMyEntityCombobox = connect(
  createStructuredSelector({
    optionKeys: createTradingEntityListForStagingMyEntitySelector<OrderMyEntityComboboxProps>((_: {}, props) => props.orderDetail)
  }),
  null,
  null,
  { forwardRef: true }
)(DefaultFundCombobox as React.ComponentType<OrderMyEntityComboboxProps>)

/* Trading Entity by Organisation */

export interface OrganisationTradingEntityMultiComboboxProps extends MultiComboboxProps<TradingEntity, string> {
  organisationId: string | null
}

const organisationIdSelector = (_: {}, props: OrganisationTradingEntityMultiComboboxProps) => props.organisationId

const organisationTradingEntityMapStateToProps = createStructuredSelector({
  optionKeys: createOrganisationTradingEntityListSelector(organisationIdSelector),
  getOption: createEntityGetterSelector(EntityType.TRADING_ENTITY)
})

export const OrganisationTradingEntityMultiCombobox = connect(organisationTradingEntityMapStateToProps, null, null, { forwardRef: true })(
  MultiCombobox
) as React.ComponentType<Omit<OrganisationTradingEntityMultiComboboxProps, 'optionKeys' | 'getOption'>>

OrganisationTradingEntityMultiCombobox.defaultProps = {
  getOptionText: TradingEntityHelper.getName,
  noItemsText: Locale.translate(Translation.SC_N_TRADING_ENTITIES, { count: 0 })
}

/* Deal Fund */

export interface DealFundComboboxProps extends ComboboxProps<TradingEntity, string> {
  organisationId: string | null
  marketType: MarketType | null
  isin: string | null
  counterpartyId: string | null
  usSponsoredClearing: boolean
  showAlias?: boolean
}

const dealFundOrganisationIdSelector = (_: {}, props: DealFundComboboxProps) => props.organisationId
const dealFundIsinSelector = (_: {}, props: DealFundComboboxProps) => props.isin
const dealFundCounterpartyIdSelector = (_: {}, props: DealFundComboboxProps) => props.counterpartyId
const dealFundShowAliasSelector = (_: {}, props: DealFundComboboxProps) => props.showAlias
const dealFundUsSponsoredClearingSelector = (_: {}, props: DealFundComboboxProps) => props.usSponsoredClearing
const dealFundMarketTypeSelector = (_: {}, props: DealFundComboboxProps) => props.marketType

const dealFundMapStateToProps = createStructuredSelector({
  optionKeys: createDealTradingEntityListSelector(
    dealFundOrganisationIdSelector,
    dealFundIsinSelector,
    dealFundCounterpartyIdSelector,
    dealFundUsSponsoredClearingSelector,
    dealFundMarketTypeSelector,
    true
  ),
  getOption: createTradingEntityWithOptionalAliasGetterSelector(dealFundCounterpartyIdSelector, dealFundShowAliasSelector)
})

export const DealFundCombobox = connect(dealFundMapStateToProps, null, null, { forwardRef: true })(Combobox) as React.ComponentType<
  Omit<DealFundComboboxProps, 'optionKeys' | 'getOption'>
>

DealFundCombobox.defaultProps = {
  getOptionText: TradingEntityWithAliasHelper.getName,
  noItemsText: Locale.translate(Translation.SC_N_FUNDS, { count: 0 })
}

export const DealFundComboboxField = Field(DealFundCombobox)

/* Deal Counterparty */

export interface DealCounterpartyComboboxProps extends ComboboxProps<TradingEntity, string> {
  organisationId: string | null
  isin: string | null
  fundId: string | null
  usSponsoredClearing: boolean
  showAlias?: boolean
  marketType: MarketType | null
}

const dealCounterpartyOrganisationIdSelector = (_: {}, props: DealCounterpartyComboboxProps) => props.organisationId
const dealCounterpartyIsinSelector = (_: {}, props: DealCounterpartyComboboxProps) => props.isin
const dealCounterpartyFundIdSelector = (_: {}, props: DealCounterpartyComboboxProps) => props.fundId
const dealCounterpartyShowAliasSelector = (_: {}, props: DealCounterpartyComboboxProps) => props.showAlias
const dealCounterpartyUsSponsoredClearingSelector = (_: {}, props: DealCounterpartyComboboxProps) => props.usSponsoredClearing
const dealCounterpartyMarketTypeSelector = (_: {}, props: DealCounterpartyComboboxProps) => props.marketType

const dealCounterpartyMapStateToProps = createStructuredSelector({
  optionKeys: createDealTradingEntityListSelector(
    dealCounterpartyOrganisationIdSelector,
    dealCounterpartyIsinSelector,
    dealCounterpartyFundIdSelector,
    dealCounterpartyUsSponsoredClearingSelector,
    dealCounterpartyMarketTypeSelector,
    false
  ),
  getOption: createTradingEntityWithOptionalAliasGetterSelector(dealCounterpartyFundIdSelector, dealCounterpartyShowAliasSelector)
})

export const DealCounterpartyCombobox = connect(dealCounterpartyMapStateToProps, null, null, { forwardRef: true })(
  Combobox
) as React.ComponentType<Omit<DealCounterpartyComboboxProps, 'optionKeys' | 'getOption'>>

DealCounterpartyCombobox.defaultProps = {
  getOptionText: TradingEntityWithAliasHelper.getName,
  noItemsText: Locale.translate(Translation.SC_N_COUNTERPARTIES, { count: 0 })
}

export const DealCounterpartyComboboxField = Field(DealCounterpartyCombobox)

/* User Counterparty */

export interface UserCounterpartyMultiComboboxProps extends MultiComboboxProps<TradingEntity, string> {
  userId: string | null
}

const userCounterpartyUserIdSelector = (_: {}, props: UserCounterpartyMultiComboboxProps) => props.userId

const userCounterpartyMapStateToProps = createStructuredSelector({
  optionKeys: createUserTradingEntityListSelector(userCounterpartyUserIdSelector),
  getOption: createEntityGetterSelector(EntityType.TRADING_ENTITY)
})

export const UserCounterpartyMultiCombobox = connect(userCounterpartyMapStateToProps, null, null, { forwardRef: true })(
  MultiCombobox
) as React.ComponentType<Omit<UserCounterpartyMultiComboboxProps, 'optionKeys' | 'getOption'>>

UserCounterpartyMultiCombobox.defaultProps = {
  getOptionText: TradingEntityHelper.getName,
  noItemsText: Locale.translate(Translation.SC_N_COUNTERPARTIES, { count: 0 })
}

export const UserCounterpartyMultiComboboxField = Field(UserCounterpartyMultiCombobox)
